import React, { useEffect, useState } from "react";
import "./PrestationStats.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const PrestationStats = () => {
  const [workers, setWorkers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("chiffreAffaire");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  // Prestation type selection
  const [prestationTypes, setPrestationTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");

  // Date filtering states
  const [filterType, setFilterType] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedWeekStart, setSelectedWeekStart] = useState("");
  const [selectedWeekEnd, setSelectedWeekEnd] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [availableYears, setAvailableYears] = useState([]);

  // Load prestation types and available years on mount
  useEffect(() => {
    fetchPrestationTypes();
    fetchAvailableYears();
  }, []);

  // Reload data when filters or selected type change
  useEffect(() => {
    if (selectedType) {
      fetchWorkers();
      fetchGroups();
    } else {
      setWorkers([]);
      setGroups([]);
    }
  }, [selectedType, filterType, selectedDate, selectedWeekStart, selectedWeekEnd, selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrestationTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/prestation-stats/types`);
      if (response.ok) {
        const types = await response.json();
        setPrestationTypes(types);
      }
    } catch (err) {
      console.error("Error fetching prestation types:", err);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const response = await fetch(`${API_URL}/api/prestation-stats/years`);
      if (response.ok) {
        const years = await response.json();
        setAvailableYears(years);
      }
    } catch (err) {
      console.error("Error fetching available years:", err);
    }
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append("prestationType", selectedType);

    if (filterType === "day" && selectedDate) {
      params.append("date", selectedDate);
      params.append("filterType", "day");
    } else if (filterType === "week" && selectedWeekStart && selectedWeekEnd) {
      params.append("weekStart", selectedWeekStart);
      params.append("weekEnd", selectedWeekEnd);
      params.append("filterType", "week");
    } else if (filterType === "month" && selectedMonth && selectedYear) {
      params.append("month", selectedMonth);
      params.append("year", selectedYear);
      params.append("filterType", "month");
    } else if (filterType === "year" && selectedYear) {
      params.append("year", selectedYear);
      params.append("filterType", "year");
    }

    return params.toString();
  };

  const fetchWorkers = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildParams();
      const response = await fetch(`${API_URL}/api/prestation-stats?${qs}`);
      if (!response.ok) throw new Error("Erreur lors du chargement");
      const data = await response.json();
      setWorkers(data);
    } catch (err) {
      console.error("Error fetching workers:", err);
      setError(err.message);
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const qs = buildParams();
      const response = await fetch(`${API_URL}/api/prestation-stats/groups?${qs}`);
      if (!response.ok) throw new Error("Erreur lors du chargement des groupes");
      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching groups:", err);
      setGroups([]);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const sortedAndFiltered = React.useMemo(() => {
    let filtered = workers;
    if (searchTerm) {
      filtered = workers.filter(p =>
        p.nom.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const mult = sortDirection === "asc" ? 1 : -1;
      if (typeof aVal === "string") return aVal.localeCompare(bVal) * mult;
      return (aVal - bVal) * mult;
    });
    return sorted;
  }, [workers, sortField, sortDirection, searchTerm]);

  const totals = React.useMemo(() => {
    return sortedAndFiltered.reduce((acc, p) => {
      acc.chantiers += p.nombreChantiers;
      acc.chiffre += p.chiffreAffaire;
      acc.marge += p.marge;
      return acc;
    }, { chantiers: 0, chiffre: 0, marge: 0 });
  }, [sortedAndFiltered]);

  const groupTotals = React.useMemo(() => {
    return groups.reduce((acc, g) => {
      acc.chantiers += g.nombreChantiers;
      acc.chiffre += g.chiffreAffaire;
      acc.marge += g.marge;
      return acc;
    }, { chantiers: 0, chiffre: 0, marge: 0 });
  }, [groups]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  const getMargeColor = (percent) => {
    if (percent >= 30) return "#10b981";
    if (percent >= 20) return "#f59e0b";
    return "#ef4444";
  };

  const getFilterLabel = () => {
    switch (filterType) {
      case "day":
        return selectedDate ? `Jour: ${new Date(selectedDate).toLocaleDateString('fr-FR')}` : "Choisir un jour";
      case "week":
        return selectedWeekStart && selectedWeekEnd
          ? `Semaine: ${new Date(selectedWeekStart).toLocaleDateString('fr-FR')} - ${new Date(selectedWeekEnd).toLocaleDateString('fr-FR')}`
          : "Choisir une semaine";
      case "month":
        return selectedMonth && selectedYear ? `Mois: ${getMonthName(selectedMonth)} ${selectedYear}` : "Choisir un mois";
      case "year":
        return selectedYear ? `Année: ${selectedYear}` : "Choisir une année";
      default:
        return "Toutes les données";
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[parseInt(monthNumber) - 1] || '';
  };

  const getWeekStartDate = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  const getWeekEndDate = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) + 6;
    const sunday = new Date(d.setDate(diff));
    return sunday.toISOString().split('T')[0];
  };

  const handleWeekDateChange = (date) => {
    if (date) {
      setSelectedWeekStart(getWeekStartDate(date));
      setSelectedWeekEnd(getWeekEndDate(date));
    } else {
      setSelectedWeekStart("");
      setSelectedWeekEnd("");
    }
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    if (type !== "day") setSelectedDate("");
    if (type !== "week") {
      setSelectedWeekStart("");
      setSelectedWeekEnd("");
    }
    if (type !== "month") setSelectedMonth("");
    if (type === "all") {
      setSelectedDate("");
      setSelectedWeekStart("");
      setSelectedWeekEnd("");
      setSelectedMonth("");
      setSelectedYear(new Date().getFullYear().toString());
    }
  };

  const displayTypeName = selectedType
    ? selectedType.charAt(0).toUpperCase() + selectedType.slice(1)
    : "Prestation";

  const renderPerformanceBadge = (margePercent) => {
    if (margePercent >= 30) return <span className="perf-badge excellent">⭐ Excellent</span>;
    if (margePercent >= 20) return <span className="perf-badge good">✅ Bon</span>;
    if (margePercent >= 10) return <span className="perf-badge average">⚠️ Moyen</span>;
    return <span className="perf-badge poor">❌ Faible</span>;
  };

  const renderTable = (data, isGroup) => {
    const label = isGroup ? "Groupe" : `Nom`;
    const avatar = isGroup ? "👥" : "👷";
    const tTotals = isGroup ? groupTotals : totals;

    return (
      <div className="pstats-table-wrapper">
        {isGroup && <h2 className="pstats-section-title">Groupes ({displayTypeName}) — travaillant ensemble</h2>}
        {!isGroup && <h2 className="pstats-section-title">Individuels ({displayTypeName})</h2>}
        <table className="pstats-table">
          <thead>
            <tr>
              <th onClick={() => !isGroup && handleSort("nom")} className={isGroup ? "" : "sortable"}>
                <span>{label}</span>
                {!isGroup && sortField === "nom" && (
                  <span className="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => !isGroup && handleSort("nombreChantiers")} className={isGroup ? "" : "sortable"}>
                <span>Nombre de Chantiers</span>
                {!isGroup && sortField === "nombreChantiers" && (
                  <span className="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => !isGroup && handleSort("chiffreAffaire")} className={isGroup ? "" : "sortable"}>
                <span>Chiffre d'Affaires</span>
                {!isGroup && sortField === "chiffreAffaire" && (
                  <span className="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => !isGroup && handleSort("marge")} className={isGroup ? "" : "sortable"}>
                <span>Marge Brute</span>
                {!isGroup && sortField === "marge" && (
                  <span className="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th>Performance</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} className="pstats-row">
                <td className="pstats-name">
                  <div className="name-cell">
                    <span className="pstats-avatar">{avatar}</span>
                    <span className="name-text">{item.nom}</span>
                  </div>
                </td>
                <td className="chantiers-count">
                  <span className="count-badge">{item.nombreChantiers}</span>
                </td>
                <td className="chiffre-affaire">
                  <span className="amount positive">{formatCurrency(item.chiffreAffaire)}</span>
                </td>
                <td className="marge">
                  <div className="marge-cell">
                    <span className="marge-amount" style={{ color: item.marge >= 0 ? "#10b981" : "#ef4444" }}>
                      {formatCurrency(item.marge)}
                    </span>
                    <span className="marge-percent" style={{ color: getMargeColor(item.margePercent), fontWeight: 600 }}>
                      ({formatPercent(item.margePercent)})
                    </span>
                  </div>
                </td>
                <td className="performance">
                  <div className="performance-indicator">
                    {renderPerformanceBadge(item.margePercent)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && (
            <tfoot>
              <tr className="totals-row">
                <td className="total-label">Total</td>
                <td className="total-value">
                  <span className="count-badge total">{tTotals.chantiers}</span>
                </td>
                <td className="total-value">
                  <span className="amount total">{formatCurrency(tTotals.chiffre)}</span>
                </td>
                <td className="total-value">
                  <div className="marge-cell">
                    <span className="marge-amount total" style={{ color: tTotals.marge >= 0 ? "#10b981" : "#ef4444" }}>
                      {formatCurrency(tTotals.marge)}
                    </span>
                    <span className="marge-percent" style={{
                      color: getMargeColor(tTotals.chiffre > 0 ? (tTotals.marge / tTotals.chiffre) * 100 : 0),
                      fontWeight: 700
                    }}>
                      ({formatPercent(tTotals.chiffre > 0 ? (tTotals.marge / tTotals.chiffre) * 100 : 0)})
                    </span>
                  </div>
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  return (
    <div className="pstats-container">
      <div className="pstats-header">
        <div className="header-content">
          <h1 className="pstats-title">
            <span className="title-icon">📊</span>
            Statistiques par Prestation
          </h1>
          <p className="pstats-subtitle">
            Analyse de performance par type de prestation
          </p>
        </div>

        <div className="header-stats">
          <div className="stat-card clear">
            <div className="stat-icon">👷</div>
            <div className="stat-content">
              <div className="stat-value">{workers.length}</div>
              <div className="stat-label">{displayTypeName} actifs</div>
            </div>
          </div>
          <div className="stat-card clear">
            <div className="stat-icon">🏗️</div>
            <div className="stat-content">
              <div className="stat-value">{totals.chantiers}</div>
              <div className="stat-label">Chantiers total</div>
            </div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">{formatCurrency(totals.chiffre)}</div>
              <div className="stat-label">Chiffre d'affaires</div>
            </div>
          </div>
        </div>
      </div>

      <div className="pstats-controls">
        {/* Prestation type selector */}
        <div className="type-selector">
          <span className="filter-label">🏷️ Type de prestation:</span>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select type-select"
          >
            <option value="">-- Choisir un type --</option>
            {prestationTypes.map((pt) => (
              <option key={pt.id} value={pt.name.toLowerCase()}>
                {pt.name}
              </option>
            ))}
          </select>
        </div>

        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder={`Rechercher un ${displayTypeName.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="date-filters">
          <div className="filter-type-selector">
            <span className="filter-label">📅 Filtrer par:</span>
            <select
              value={filterType}
              onChange={(e) => handleFilterTypeChange(e.target.value)}
              className="filter-select"
            >
              <option value="all">Toutes les données</option>
              <option value="day">Par jour</option>
              <option value="week">Par semaine</option>
              <option value="month">Par mois</option>
              <option value="year">Par année</option>
            </select>
          </div>

          {filterType === "day" && (
            <div className="date-input-group">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-input"
              />
            </div>
          )}

          {filterType === "week" && (
            <div className="date-input-group">
              <label className="week-label">Choisir une date dans la semaine:</label>
              <input
                type="date"
                onChange={(e) => handleWeekDateChange(e.target.value)}
                className="date-input"
              />
              {selectedWeekStart && selectedWeekEnd && (
                <div className="week-range">
                  <span className="week-info">
                    📅 Semaine du {new Date(selectedWeekStart).toLocaleDateString('fr-FR')}
                    {" "}au {new Date(selectedWeekEnd).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>
          )}

          {filterType === "month" && (
            <div className="date-input-group">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="month-select"
              >
                <option value="">Choisir un mois</option>
                <option value="1">Janvier</option>
                <option value="2">Février</option>
                <option value="3">Mars</option>
                <option value="4">Avril</option>
                <option value="5">Mai</option>
                <option value="6">Juin</option>
                <option value="7">Juillet</option>
                <option value="8">Août</option>
                <option value="9">Septembre</option>
                <option value="10">Octobre</option>
                <option value="11">Novembre</option>
                <option value="12">Décembre</option>
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="year-select"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}

          {filterType === "year" && (
            <div className="date-input-group">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="year-select"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}

          <div className="current-filter">
            <span className="filter-badge">{getFilterLabel()}</span>
          </div>
        </div>

        <button className="refresh-btn" onClick={() => { fetchWorkers(); fetchGroups(); }}>
          🔄 Actualiser
        </button>
      </div>

      {!selectedType && (
        <div className="empty-state">
          <div className="empty-icon">🏷️</div>
          <h3>Sélectionnez un type de prestation</h3>
          <p>Choisissez un type de prestation ci-dessus pour afficher les statistiques</p>
        </div>
      )}

      {selectedType && loading && (
        <div className="pstats-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des données...</p>
        </div>
      )}

      {selectedType && error && (
        <div className="pstats-error">
          <p>❌ {error}</p>
          <button onClick={() => { fetchWorkers(); fetchGroups(); }} className="retry-btn">
            Réessayer
          </button>
        </div>
      )}

      {selectedType && !loading && !error && (
        <>
          {groups && groups.length > 0 && renderTable(groups, true)}

          {sortedAndFiltered.length === 0 && groups.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>Aucun résultat trouvé</h3>
              <p>
                {searchTerm
                  ? "Aucun résultat ne correspond à votre recherche"
                  : `Aucun ${displayTypeName.toLowerCase()} n'a été assigné aux chantiers`}
              </p>
            </div>
          ) : (
            sortedAndFiltered.length > 0 && renderTable(sortedAndFiltered, false)
          )}
        </>
      )}
    </div>
  );
};

export default PrestationStats;
